// Fase 5 · Mapeo de fuentes (SignalR, hubs, dedupe, navegador) a eventos
// del grabador de depuración. Puro donde se puede; `installDebugInstrumentation`
// engancha los observadores una sola vez por pestaña.

import { record } from "./debugRecorder";
import { installClickTracing, setRoutePath } from "./debugTrace";
import { setHubLifecycleObserver } from "../hubs/HubConnectionManager";
import { setDedupeObserver } from "../requestDedupe";

/** Campos del evento "hub_in" para un sobre de `/hub/tpv`. */
export function hubInFields(env, myDeviceId) {
  const origin = env?.originDeviceId || null;
  const mine = !!origin && !!myDeviceId && String(origin).toLowerCase() === String(myDeviceId).toLowerCase();
  return {
    // Un evento recibido no pertenece a la acción local en curso (lo ha
    // causado quien diga originDeviceId); el panel lo enlaza por seq o por
    // `causeActionId` (la acción que lo causó, si el sobre la trae).
    actionId: null,
    actionName: null,
    eventType: env?.type || "unknown",
    seq: env?.seq ?? null,
    target: env?.id != null ? String(env.id) : null,
    detail: { originDeviceId: origin, mine, epoch: env?.epoch || null, causeActionId: env?.actionId || null },
  };
}

/** Nombre corto del hub a partir de su URL (…/hub/tpv?… → "tpv"). */
export function hubNameOf(url) {
  const m = String(url || "").match(/\/hub\/([^/?#]+)/i);
  return m ? m[1] : String(url || "").slice(0, 60);
}

// Estado del hub en el vocabulario que usa el panel para pintar las franjas
// de desconexión: caída = disconnected | reconnecting | closed;
// vuelta = connected | reconnected.
const CONN_STATE_BY_EVENT = {
  reconnecting: "reconnecting",
  closed: "closed",
  start_failed: "disconnected",
  connected: "connected",
  reconnected: "reconnected",
  restarted: "reconnected", // conexión nueva tras onclose/watchdog
};

export function connStateOf(event) {
  return CONN_STATE_BY_EVENT[event] || "disconnected";
}

/** Campos del evento "conn" para un cambio de estado del hub. */
export function connFields(ev) {
  const hub = hubNameOf(ev?.url);
  const state = connStateOf(ev?.event);
  const isError = state === "closed" || state === "disconnected" || state === "reconnecting";
  return {
    actionId: null,
    actionName: null,
    eventType: state,
    target: hub,
    durationMs: ev?.durationMs ?? null,
    isError,
    detail: {
      hub,
      event: ev?.event || null,
      downtimeMs: state === "reconnected" ? ev?.durationMs ?? null : null,
      reason: ev?.reason || null,
      error: ev?.error || null,
      lifeSec: ev?.lifeSec ?? null,
      failures: ev?.failures ?? null,
    },
  };
}

/** Fila para la pestaña "Hub" del monitor (slice `errors.hubError`). */
export function hubErrorRow(ev) {
  return {
    url: ev?.url,
    hub: hubNameOf(ev?.url),
    event: ev?.event,
    message: ev?.error || ev?.event,
    downtimeMs: ev?.durationMs ?? null,
    at: new Date().toISOString(),
  };
}

// ── Instalación (una vez por pestaña) ────────────────────────────────

let installed = false;

/**
 * `dispatchHubError(row)` se usa para rellenar la pestaña Hub del monitor
 * (solo reconexiones/cierres/fallos: pocos y relevantes).
 */
export function installDebugInstrumentation({ dispatchHubError, isMonitoringOn } = {}) {
  if (installed || typeof window === "undefined") return;
  installed = true;

  installClickTracing((info, actionId) => {
    record("ui", {
      actionId,
      actionName: info.name,
      screen: info.screen,
      x: info.x,
      y: info.y,
      viewportW: info.viewportW,
      viewportH: info.viewportH,
      target: info.target,
    });
  });

  setHubLifecycleObserver((ev) => {
    const recorded = record("conn", connFields(ev));
    const relevant = ev?.event === "reconnecting" || ev?.event === "reconnected"
      || ev?.event === "closed" || ev?.event === "start_failed";
    if (relevant && dispatchHubError && (recorded || isMonitoringOn?.())) {
      try { dispatchHubError(hubErrorRow(ev)); } catch { /* noop */ }
    }
  });

  setDedupeObserver(({ url }) => {
    record("dup", { eventType: "GET_COALESCED", target: String(url || "").slice(0, 200) });
  });

  // Ciclo de vida de la página.
  let navType = "navigate";
  try {
    const nav = window.performance?.getEntriesByType?.("navigation")?.[0];
    if (nav?.type) navType = nav.type; // navigate | reload | back_forward
  } catch { /* noop */ }
  record("lifecycle", { actionId: null, actionName: null, eventType: `page.${navType}` });

  document.addEventListener("visibilitychange", () => {
    record("lifecycle", { actionId: null, actionName: null, eventType: `visibility.${document.visibilityState}` });
  });
  // Red del navegador: también como "conn" (target "network") para que el
  // panel pinte la franja de desconexión aunque el hub aún no lo haya notado.
  let offlineAt = null;
  window.addEventListener("online", () => {
    const downtimeMs = offlineAt ? Date.now() - offlineAt : null;
    offlineAt = null;
    record("lifecycle", { actionId: null, actionName: null, eventType: "network.online" });
    record("conn", { actionId: null, actionName: null, eventType: "connected", target: "network", durationMs: downtimeMs, detail: { downtimeMs } });
  });
  window.addEventListener("offline", () => {
    offlineAt = Date.now();
    record("lifecycle", { actionId: null, actionName: null, eventType: "network.offline", isError: true });
    record("conn", { actionId: null, actionName: null, eventType: "disconnected", target: "network", isError: true });
  });
}

/** Cambio de ruta: actualiza la pantalla y deja rastro de navegación. */
export function trackRoute(pathname) {
  setRoutePath(pathname);
  record("lifecycle", { actionId: null, actionName: null, eventType: "route", target: String(pathname || "").slice(0, 200) });
}
